import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { DAOProposal } from '../../types/blockchain';
import blockchainService from '../../services/blockchainService';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  PlusIcon 
} from '@heroicons/react/24/outline';

const DAOGovernance: React.FC = () => {
  const { wallet } = useWeb3();
  const [proposals, setProposals] = useState<DAOProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [voting, setVoting] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    setLoading(true);
    try {
      // 使用新的统一mock数据
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      const proposalsData = mockData.getDAOProposals();
      setProposals(proposalsData);
    } catch (error) {
      console.error('Failed to load proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (proposalId: string, support: boolean) => {
    if (!wallet?.address) return;
    
    setVoting(proposalId);
    try {
      await blockchainService.voteOnProposal(proposalId, support);
      // 更新提案数据
      setProposals(prev => prev.map(p => 
        p.id === proposalId 
          ? { 
              ...p, 
              votesFor: p.votesFor + (support ? 1 : 0),
              votesAgainst: p.votesAgainst + (support ? 0 : 1),
              totalVotes: p.totalVotes + 1
            }
          : p
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setVoting(null);
    }
  };

  const handleCreateProposal = async (formData: any) => {
    if (!wallet?.address) return;
    
    try {
      const proposal = await blockchainService.createProposal({
        title: formData.title,
        description: formData.description,
        proposer: 'Current User',
        proposerAddress: wallet.address,
        type: formData.type,
        startTime: Date.now(),
        endTime: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7天后
      });
      
      setProposals(prev => [proposal, ...prev]);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create proposal:', error);
    }
  };

  const getStatusIcon = (status: DAOProposal['status']) => {
    switch (status) {
      case 'active':
        return <ClockIcon className="w-5 h-5 text-blue-500" />;
      case 'passed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'executed':
        return <CheckCircleIcon className="w-5 h-5 text-purple-500" />;
    }
  };

  const getStatusColor = (status: DAOProposal['status']) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'passed': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'executed': return 'text-purple-600 bg-purple-100';
    }
  };

  const getTypeColor = (type: DAOProposal['type']) => {
    switch (type) {
      case 'governance': return 'text-blue-600 bg-blue-100';
      case 'treasury': return 'text-green-600 bg-green-100';
      case 'technical': return 'text-purple-600 bg-purple-100';
    }
  };

  const formatTimeRemaining = (endTime: number) => {
    const remaining = endTime - Date.now();
    if (remaining <= 0) return '已结束';
    
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    
    if (days > 0) return `${days}天${hours}小时`;
    return `${hours}小时`;
  };

  const CreateProposalForm: React.FC = () => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      type: 'governance' as DAOProposal['type']
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleCreateProposal(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6">创建新提案</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">提案标题</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">提案描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                rows={4}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">提案类型</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value as DAOProposal['type']})}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="governance">治理</option>
                <option value="treasury">财务</option>
                <option value="technical">技术</option>
              </select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
              >
                取消
              </button>
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                创建提案
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以参与 DAO 治理</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">DAO 治理</h1>
            <p className="text-gray-600">参与社区治理，共同决定平台发展方向</p>
            
            {/* DAO治理核心价值展示 */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h3 className="font-semibold text-emerald-800 mb-2">🗳️ 去中心化决策</h3>
                <p className="text-sm text-emerald-700">社区成员通过代币投票参与平台重要决策，确保平台发展符合用户利益。</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <h3 className="font-semibold text-rose-800 mb-2">🎯 透明治理</h3>
                <p className="text-sm text-rose-700">所有提案和投票过程公开透明，链上记录不可篡改，保障治理公正性。</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            创建提案
          </button>
        </div>
      </div>

      {/* 统计信息 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-2">活跃提案</h3>
          <p className="text-3xl font-bold text-blue-600">
            {proposals.filter(p => p.status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-2">已通过提案</h3>
          <p className="text-3xl font-bold text-green-600">
            {proposals.filter(p => p.status === 'passed').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-2">总投票数</h3>
          <p className="text-3xl font-bold text-purple-600">
            {proposals.reduce((sum, p) => sum + p.totalVotes, 0)}
          </p>
        </div>
      </div>

      {/* 提案列表 */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">加载中...</p>
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            暂无提案
          </div>
        ) : (
          proposals.map((proposal) => (
            <div key={proposal.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(proposal.status)}
                    <h3 className="text-xl font-semibold">{proposal.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(proposal.status)}`}>
                      {proposal.status === 'active' ? '投票中' :
                       proposal.status === 'passed' ? '已通过' :
                       proposal.status === 'rejected' ? '已拒绝' : '已执行'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(proposal.type)}`}>
                      {proposal.type === 'governance' ? '治理' :
                       proposal.type === 'treasury' ? '财务' : '技术'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{proposal.description}</p>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <span>提案人: {proposal.proposer}</span>
                    <span>
                      {proposal.status === 'active' 
                        ? `剩余时间: ${formatTimeRemaining(proposal.endTime)}`
                        : `结束于: ${new Date(proposal.endTime).toLocaleDateString()}`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* 投票统计 */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">投票统计</span>
                  <span className="text-sm text-gray-600">总票数: {proposal.totalVotes}</span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div className="flex h-full rounded-full overflow-hidden">
                    <div 
                      className="bg-green-500"
                      style={{ width: `${proposal.totalVotes > 0 ? (proposal.votesFor / proposal.totalVotes) * 100 : 0}%` }}
                    />
                    <div 
                      className="bg-red-500"
                      style={{ width: `${proposal.totalVotes > 0 ? (proposal.votesAgainst / proposal.totalVotes) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">支持: {proposal.votesFor}</span>
                  <span className="text-red-600">反对: {proposal.votesAgainst}</span>
                </div>
              </div>

              {/* 投票按钮 */}
              {proposal.status === 'active' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleVote(proposal.id, true)}
                    disabled={voting === proposal.id}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <HandThumbUpIcon className="w-4 h-4" />
                    {voting === proposal.id ? '投票中...' : '支持'}
                  </button>
                  <button
                    onClick={() => handleVote(proposal.id, false)}
                    disabled={voting === proposal.id}
                    className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <HandThumbDownIcon className="w-4 h-4" />
                    {voting === proposal.id ? '投票中...' : '反对'}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showCreateForm && <CreateProposalForm />}
    </div>
  );
};

export default DAOGovernance;
